/** What an issue will freeze, said above the button and again in the drawer that confirms it
 * (design review 2026-11-03, persona #10).
 *
 * A primary button sat under a red "not ready" banner and an unapproved path, and the consequence was
 * a sentence below it. Issuing regardless of the checks is correct by design (ADR-0020: whether a
 * report may lawfully be filed is a person's judgement, and the verdict travels inside the artifact),
 * but the person was not shown what they were about to freeze. This is that: one line, from the same
 * two reads the filing's page uses (the checks, for the verdict and the counts; the report, for the
 * stated total), and **nothing is computed here** - the words are the server's and the total is its
 * string.
 *
 * **It does not gate.** It states; the button still issues. A reason for issuing a filing that is not
 * approved would be a new field on the issued record and is a decision no accepted ADR covers
 * (`docs/adr/0219-*`, Proposed), so the drawer confirms and does not ask.
 */

import type { Scope2Method } from "../../api/queries";
import { Drawer } from "../../components/ui/Drawer";
import styles from "./FilingParts.module.css";

export function IssueSnapshot({ parts }: { parts: string[] | undefined }) {
  return (
    <div className={styles.snapshot} role="group" aria-label="What will be issued" data-testid="issue-snapshot">
      <span className={styles.snapshotLabel}>Will be issued as</span>
      <strong className={styles.snapshotText} data-testid="issue-snapshot-text">
        {parts ? parts.join(" · ") : "Reading the checks and the figures"}
      </strong>
    </div>
  );
}

export function IssueConfirmDrawer({
  open,
  onClose,
  onConfirm,
  parts,
  pending,
  method,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  parts: string[] | undefined;
  pending: boolean;
  method: Scope2Method;
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Issue this filing"
      width="sm"
      testId="issue-confirm-drawer"
      footer={
        <>
          <button
            type="button"
            className={styles.primary}
            onClick={onConfirm}
            disabled={pending || !parts}
            data-testid="issue-confirm"
          >
            {pending ? "Issuing" : `Issue ${method}-based`}
          </button>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className={styles.drawerBody}>
        <IssueSnapshot parts={parts} />
        <p className={styles.quiet}>
          This freezes the figures as they stand now. It cannot be changed afterwards, and a correction
          is a new filing. EmitIQ sends nothing to a regulator.
        </p>
      </div>
    </Drawer>
  );
}
